import axios from 'axios';

const api = axios.create({ baseURL: '/api' });

// Injecte le JWT sur chaque requête
api.interceptors.request.use(config => {
  const token = localStorage.getItem('token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// Étapes de connexion : un 401 y est une réponse attendue (identifiants, code MFA),
// pas une session expirée — ne pas recharger la page de login.
const AUTH_STEPS = ['/auth/login', '/auth/mfa/setup', '/auth/mfa/verify'];

// Redirige vers /login si 401
api.interceptors.response.use(
  res => res,
  err => {
    if (err.response?.status === 401 && !AUTH_STEPS.includes(err.config?.url)) {
      localStorage.removeItem('token');
      localStorage.removeItem('role');
      window.location.href = '/login';
    }
    return Promise.reject(err);
  }
);

// ─── Auth ───────────────────────────────────────────────────────────────────
export const login = (username, password) =>
  api.post('/auth/login', { username, password }).then(r => r.data);

// MFA (DO-207) : second temps du login, avec le mfaToken renvoyé par /auth/login
export const mfaSetup = mfaToken =>
  api.post('/auth/mfa/setup', { mfaToken }).then(r => r.data);

export const mfaVerify = (mfaToken, code, trustBrowser) =>
  api.post('/auth/mfa/verify', { mfaToken, code, trustBrowser }).then(r => r.data);

export const changePassword = (currentPassword, newPassword) =>
  api.post('/auth/change-password', { currentPassword, newPassword }).then(r => r.data);

// ─── Dashboard ──────────────────────────────────────────────────────────────
export const getDashboard = () =>
  api.get('/dashboard').then(r => r.data);

export const getHistory = (licenseId, days = 30) =>
  api.get(`/dashboard/history/${licenseId}?days=${days}`).then(r => r.data);

// ─── Licences ────────────────────────────────────────────────────────────────
export const getLicenses = () =>
  api.get('/licenses').then(r => r.data);

export const getLicense = id =>
  api.get(`/licenses/${id}`).then(r => r.data);

export const createLicense = data =>
  api.post('/licenses', data).then(r => r.data);

export const activateLicense = id =>
  api.put(`/licenses/${id}/activate`).then(r => r.data);

export const deactivateLicense = id =>
  api.put(`/licenses/${id}/deactivate`).then(r => r.data);

// Code de licence (SNCF, CA31…) : identifiant utilisé par la CI, l'agent de push et l'API
export const updateLicenseName = (id, clientName) =>
  api.put(`/licenses/${id}/client-name`, { clientName }).then(r => r.data);

export const updateLicenseCode = (id, code) =>
  api.put(`/licenses/${id}/code`, { code }).then(r => r.data);

// ─── Instances ───────────────────────────────────────────────────────────────
export const getInstances = () =>
  api.get('/instances').then(r => r.data);

export const getInstancesByLicense = licenseId =>
  api.get(`/instances/by-license/${licenseId}`).then(r => r.data);

export const disableInstance = (id, reason) =>
  api.put(`/instances/${id}/disable`, { reason }).then(r => r.data);

export const enableInstance = id =>
  api.put(`/instances/${id}/enable`).then(r => r.data);

export const setInstanceEnvironment = (id, environment) =>
  api.put(`/instances/${id}/environment`, null, { params: { environment } }).then(r => r.data);

// DO-207 : approbation des nouvelles instances, jeton propre à chaque instance
export const approveInstance = id =>
  api.put(`/instances/${id}/approve`).then(r => r.data);

export const resetInstanceToken = id =>
  api.put(`/instances/${id}/reset-token`).then(r => r.data);

// Réglages de sécurité effectifs (MFA, IP des robots, mode transitoire des instances)
export const getSecurityPolicy = () =>
  api.get('/security/policy').then(r => r.data);

// ─── Tentatives de connexion refusées ────────────────────────────────────────
// Paginé : params { status: PENDING | DECLARED | IGNORED | RESOLVED | ALL, page, size }
export const getConnectionAttempts = params =>
  api.get('/connection-attempts', { params }).then(r => r.data);

export const declareAttempt = (id, licenseData) =>
  api.post(`/connection-attempts/${id}/declare`, licenseData).then(r => r.data);

export const ignoreAttempt = id =>
  api.post(`/connection-attempts/${id}/ignore`).then(r => r.data);

// ─── Update Packages ─────────────────────────────────────────────────────────
// Liste paginée / triée / filtrée (page Packages) — params : client (id | global),
// environment (… | none), state (ACTIVE | DELETED | ALL), q, uploadedBy, page, size, sort, dir
export const searchPackages = params =>
  api.get('/packages', { params }).then(r => r.data);

// Packages visibles d'une licence (globaux inclus), paginé : params { environment, q, name,
// version, page, size, sort, dir }
export const getPackagesForLicense = (licenseId, params) =>
  api.get(`/packages/license/${licenseId}`, { params }).then(r => r.data);

export const uploadPackage = formData =>
  api.post('/packages', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }).then(r => r.data);

export const downloadPackage = id =>
  api.get(`/packages/${id}/download`, { responseType: 'blob' }).then(r => r.data);

export const deletePackage = id =>
  api.delete(`/packages/${id}`).then(r => r.data);

// ─── Instance Updates ────────────────────────────────────────────────────────
export const requestUpdate = (instanceId, packageId, notes) =>
  api.post(`/instances/${instanceId}/request-update`, { packageId, notes }).then(r => r.data);

// Historiques paginés (plus récent en premier) : params { page, size }
export const getInstanceUpdateHistory = (instanceId, params) =>
  api.get(`/instances/${instanceId}/updates`, { params }).then(r => r.data);

export const getLicenseUpdateHistory = (licenseId, params) =>
  api.get(`/licenses/${licenseId}/updates`, { params }).then(r => r.data);

// ─── Utilisateurs (ADMIN uniquement, DO-208) ─────────────────────────────────
export const getUsers = () =>
  api.get('/users').then(r => r.data);

export const createUser = data =>
  api.post('/users', data).then(r => r.data);

export const updateUser = (id, data) =>
  api.put(`/users/${id}`, data).then(r => r.data);

export const blockUser = id =>
  api.put(`/users/${id}/block`).then(r => r.data);

export const unblockUser = id =>
  api.put(`/users/${id}/unblock`).then(r => r.data);

export const resetUserPassword = (id, newPassword) =>
  api.post(`/users/${id}/reset-password`, { newPassword }).then(r => r.data);

// Compte robot : nouvelle clé, renvoyée une seule fois ({ key })
export const regenerateAgentKey = id =>
  api.post(`/users/${id}/regenerate-key`).then(r => r.data);

// Authentificateur perdu : nouvel enrôlement MFA à la prochaine connexion
export const resetUserMfa = id =>
  api.post(`/users/${id}/reset-mfa`).then(r => r.data);

// confirm = identifiant ressaisi par l'admin (vérifié aussi côté back)
export const deleteUser = (id, confirm) =>
  api.delete(`/users/${id}`, { params: { confirm } }).then(r => r.data);

// params : { userId, username, success, from, to, page, size } — tous optionnels
export const getLoginEvents = params =>
  api.get('/login-events', { params }).then(r => r.data);

// Journal d'activité des robots (DO-207) — params : { userId, username, success, from, to, page, size }
export const getRobotRequests = params =>
  api.get('/robot-requests', { params }).then(r => r.data);
